import { useCallback, useRef, useState } from "react";
import { streamArchieChat } from "../api/archieStream";
import {
  ArchieErrorPayload,
  ArchieFinalPayload,
  ArchieStreamHistoryMessage,
} from "../types/archie/archieStream";

const STREAM_SESSION_KEY = "archie_session_id";

/**
 * Streaming variant of useArchieChat for the Archie SSE endpoint (CK-2215).
 * The Lambda is stateless — the browser owns the conversation (history is re-sent per turn) and
 * `sessionId` groups turns for the server-side admin conversation viewer.
 * Streaming does not fit react-query's useMutation; the legacy hooks stay untouched for fallback.
 */

const getOrCreateSessionId = (): string => {
  let id = sessionStorage.getItem(STREAM_SESSION_KEY);
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem(STREAM_SESSION_KEY, id);
  }
  return id;
};

export interface SendStreamingArgs {
  archieUrl: string;
  message: string;
  history: ArchieStreamHistoryMessage[];
  /** Honored only for admin tokens (impersonation); ignored otherwise. */
  customerId?: string;
  /** Incremental assistant text; called per text_delta with the accumulated text so far. */
  onDelta?: (accumulatedText: string) => void;
  onFinal: (payload: ArchieFinalPayload) => void;
  onError: (payload: ArchieErrorPayload) => void;
}

export const useArchieStream = () => {
  const [isStreaming, setIsStreaming] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  /** Start a fresh server-side conversation grouping (new chat / tenant switch / user switch). */
  const resetSession = useCallback(() => {
    sessionStorage.removeItem(STREAM_SESSION_KEY);
  }, []);

  const abort = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const sendStreaming = useCallback(
    async ({
      archieUrl,
      message,
      history,
      customerId,
      onDelta,
      onFinal,
      onError,
    }: SendStreamingArgs) => {
      const controller = new AbortController();
      abortRef.current = controller;
      setIsStreaming(true);
      let accumulated = "";
      let finished = false;
      try {
        await streamArchieChat({
          baseUrl: archieUrl,
          request: {
            message,
            sessionId: getOrCreateSessionId(),
            history,
            tzOffset: new Date().getTimezoneOffset(),
            ...(customerId ? { customerId } : {}),
          },
          signal: controller.signal,
          onEvent: (event) => {
            if (event.type === "text_delta") {
              const delta = (event.data as { delta?: string }).delta ?? "";
              accumulated += delta;
              onDelta?.(accumulated);
            } else if (event.type === "final") {
              finished = true;
              onFinal(event.data as unknown as ArchieFinalPayload);
            } else if (event.type === "error") {
              finished = true;
              onError(event.data as unknown as ArchieErrorPayload);
            }
            // usage_ledger / progress / tool_call / attachment / chart / download / actions:
            // not surfaced in the v1 widget; richer handling arrives with the V2 ArchiePanel work.
          },
        });
        if (!finished) {
          // Stream ended without a final/error frame (connection cut mid-turn).
          onError({
            message:
              "The connection was interrupted before Archie finished. Please try again.",
          });
        }
      } catch (err: unknown) {
        if ((err as Error)?.name === "AbortError") return; // user cancelled — keep partial text
        onError({
          message:
            (err as Error)?.message ||
            "Sorry, something went wrong. Please try again.",
        });
      } finally {
        setIsStreaming(false);
        abortRef.current = null;
      }
    },
    []
  );

  return { isStreaming, sendStreaming, abort, resetSession };
};
